import { Box, makeStyles, Typography } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";

import { WebinarSummary } from "@/api/generated";
import {
  ExclusiveFetchError,
  ExclusivePageHeading,
  ExclusiveTemplate,
  LearningContentLink,
  useExclusiveLayout,
} from "@/components/exclusive/ExclusiveContentLayout";
import { ExclusiveVideoCard } from "@/components/exclusive/ExclusiveVideoCard";
import { Loading } from "@/components/Loading";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { NavigationPath } from "@/utilities/enums";

type SectionKey = "recent" | "list";

type SectionConfig = {
  key: SectionKey;
  title: string;
  recentOnly?: boolean;
  perPage: number;
};

type SectionState = {
  items: WebinarSummary[];
  nextPage: number;
  loading: boolean;
  hasMore: boolean;
  error: boolean;
};

const LOAD_THRESHOLD_PX = 48;

const sectionConfigs: SectionConfig[] = [
  {
    key: "recent",
    title: "新着",
    recentOnly: true,
    perPage: 5,
  },
  {
    key: "list",
    title: "すべての動画",
    perPage: 99,
  },
];

const createInitialSectionStates = (): Record<SectionKey, SectionState> =>
  sectionConfigs.reduce(
    (acc, config) => ({
      ...acc,
      [config.key]: {
        items: [],
        nextPage: 1,
        loading: false,
        hasMore: true,
        error: false,
      },
    }),
    {} as Record<SectionKey, SectionState>
  );

const useStyles = makeStyles({
  section: { display: "flex", flexDirection: "column", gap: layout.space[12], minWidth: 0 },
  carousel: {
    display: "flex",
    alignItems: "stretch",
    gap: layout.space[12],
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    paddingBottom: layout.space[4],
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    marginRight: -layout.space[24],
    paddingRight: layout.space[24],
    [theme.breakpoints.up("md")]: { gap: layout.space[24], marginRight: 0, paddingRight: 0 },
  },
  listGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: layout.space[12],
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: layout.space[24] },
  },
  empty: {
    backgroundColor: colors["surface/card"],
    borderRadius: layout.radius.md,
    padding: layout.space[24],
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
  },
  emptyTitle: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
});

export const PremiumWebinarListPage: React.FC = () => {
  const classes = useStyles();
  const common = useExclusiveLayout();
  const requests = useRef(new Set<SectionKey>());
  const history = useHistory();
  const api = useMemo(() => PropallyApiFactory(), []);
  const [sectionStates, setSectionStates] = useState<Record<SectionKey, SectionState>>(createInitialSectionStates);
  const sectionStatesRef = useRef(sectionStates);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    sectionStatesRef.current = sectionStates;
  }, [sectionStates]);

  const sectionConfigMap = useMemo(() => {
    return sectionConfigs.reduce(
      (acc, config) => ({
        ...acc,
        [config.key]: config,
      }),
      {} as Record<SectionKey, SectionConfig>
    );
  }, []);

  const loadSection = useCallback(
    async (sectionKey: SectionKey, options?: { reset?: boolean }) => {
      const config = sectionConfigMap[sectionKey];
      if (!config || requests.current.has(sectionKey)) {
        return;
      }
      const prevState = sectionStatesRef.current[sectionKey];
      const isReset = options?.reset ?? false;
      const nextPage = isReset ? 1 : (prevState?.nextPage ?? 1);
      if (!isReset && ((prevState?.loading ?? false) || !(prevState?.hasMore ?? true))) {
        return;
      }

      requests.current.add(sectionKey);
      setSectionStates((prev) => {
        const current = prev[sectionKey] ?? {
          items: [],
          nextPage: 1,
          loading: false,
          hasMore: true,
        };
        return {
          ...prev,
          [sectionKey]: {
            ...current,
            ...(isReset
              ? {
                  items: [],
                  nextPage: 1,
                  hasMore: true,
                }
              : {}),
            loading: true,
            error: false,
          },
        };
      });

      try {
        const response = await api.v1WebinarsGet(nextPage, config.perPage, config.recentOnly, {
          withCredentials: true,
        });
        const newItems = response.data.webinars ?? [];
        setSectionStates((prev) => {
          const current = prev[sectionKey] ?? {
            items: [],
            nextPage: 1,
            loading: false,
            hasMore: true,
          };
          const baseItems = isReset ? [] : current.items;
          return {
            ...prev,
            [sectionKey]: {
              items: [...baseItems, ...newItems],
              nextPage: nextPage + 1,
              loading: false,
              hasMore: newItems.length === config.perPage,
              error: false,
            },
          };
        });
      } catch (sectionError) {
        console.error(`ウェビナー取得に失敗しました(${sectionKey})`, sectionError);
        setSectionStates((prev) => {
          const current = prev[sectionKey] ?? {
            items: [],
            nextPage,
            loading: false,
            hasMore: false,
          };
          return {
            ...prev,
            [sectionKey]: {
              ...current,
              loading: false,
              error: true,
            },
          };
        });
      } finally {
        requests.current.delete(sectionKey);
      }
    },
    [api, sectionConfigMap]
  );

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        await Promise.all(sectionConfigs.map((config) => loadSection(config.key, { reset: true })));
      } finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadSection]);

  const handleNavigateDetail = useCallback(
    (webinarId: string) => {
      history.push(`/webinars/${webinarId}`);
    },
    [history]
  );

  const handleBack = useCallback(() => {
    history.replace("/mypage");
  }, [history]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      history.replace("/mypage");
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [history]);

  const handleCarouselScroll = useCallback(
    (sectionKey: SectionKey) => async (event: React.UIEvent<HTMLDivElement>) => {
      const state = sectionStatesRef.current[sectionKey];
      if (!state || state.loading || !state.hasMore) {
        return;
      }
      const target = event.currentTarget;
      const remaining = target.scrollWidth - (target.scrollLeft + target.clientWidth);
      if (remaining > LOAD_THRESHOLD_PX) {
        return;
      }
      await loadSection(sectionKey);
    },
    [loadSection]
  );

  const hasAnyWebinars = sectionConfigs.some((config) => (sectionStates[config.key]?.items.length ?? 0) > 0);
  const hasError = sectionConfigs.some((config) => sectionStates[config.key].error);
  const showEmpty = !initializing && !hasAnyWebinars && !hasError;

  return (
    <ExclusiveTemplate
      headerTitle="プロパリー限定動画"
      navigationPath={NavigationPath.HOME}
      onBack={handleBack}
      isDense
      isPcV2
    >
      <Box className={`${common.body} ${common.stack}`}>
        <ExclusivePageHeading title="プロパリー限定動画" backLabel="ホーム" onBack={handleBack} />
        {initializing ? (
          <Loading />
        ) : showEmpty ? (
          <Box className={common.empty}>
            <Box className={classes.empty}>
              <Typography component="h2" className={classes.emptyTitle}>
                いま見られる動画はありません
              </Typography>
              <Typography className={common.small}>
                新しい動画が公開されるまで、学習コンテンツの記事をご覧いただけます。
              </Typography>
            </Box>
            <LearningContentLink empty />
          </Box>
        ) : (
          <>
            {sectionConfigs.map((config) => {
              const state = sectionStates[config.key];
              if (!state.loading && state.items.length === 0 && !state.error) return null;
              return (
                <Box component="section" key={config.key} aria-label={config.title} className={classes.section}>
                  <Typography component="h2" className={common.sectionTitle}>
                    {config.title}
                  </Typography>
                  {config.key === "list" && state.items.length > 0 && (
                    <Typography className={common.small}>
                      {state.hasMore ? "動画を新しい順に表示しています" : `${state.items.length}本 ・ 新しい順`}
                    </Typography>
                  )}
                  {state.error && (
                    <ExclusiveFetchError
                      message={`${config.title}の読み込みに失敗しました。`}
                      onRetry={() => loadSection(config.key)}
                    />
                  )}
                  <Box
                    className={config.key === "list" ? classes.listGrid : classes.carousel}
                    onScroll={config.key === "recent" ? handleCarouselScroll(config.key) : undefined}
                  >
                    {state.items.map((webinar, index) => (
                      <ExclusiveVideoCard
                        key={webinar.id ?? `${config.key}-${index}`}
                        webinar={webinar}
                        variant={config.key === "recent" ? "recent" : "list"}
                        onClick={() => webinar.id && handleNavigateDetail(webinar.id)}
                      />
                    ))}
                  </Box>
                  {state.loading && <Loading />}
                </Box>
              );
            })}
            <LearningContentLink />
          </>
        )}
      </Box>
    </ExclusiveTemplate>
  );
};
